import { ConfigDB, ViewAPI, apiBase } from "../axios";
import { resolvePostGrestRequestWithPagination } from "../resolve";
import { ViewResult, ViewColumnDef } from "../../pages/audit-report/types";
import { tristateOutputToQueryFilterParam } from "../../ui/Dropdowns/TristateReactSelect";
import { buildLabelFilterQueries } from "../utils/labels";

export type SectionResultsMap = Record<string, ViewResult>;

export interface DashboardResponse extends ViewResult {
  id: string;
  /**
   * Pre-resolved section ViewResults.
   *
   * Backend metadata endpoints currently key this map by "<namespace>/<name>".
   */
  sectionResults?: SectionResultsMap;
}

/**
 * Fetches the dashboard view definition in a single call.
 * The server resolves the configured dashboard property, looks up the view,
 * and returns the full view spec including pre-resolved section results.
 *
 * Returns `null` when no default dashboard view is configured (404).
 */
export const getDashboard = async (): Promise<DashboardResponse | null> => {
  try {
    const response = await apiBase.get<DashboardResponse>("/dashboard");
    return response.data;
  } catch (err: any) {
    if (err?.response?.status === 404) {
      return null;
    }
    throw err;
  }
};

interface SectionViewRef {
  namespace?: string;
  name?: string;
}

/**
 * Returns a section result from metadata by matching a viewRef.
 *
 * Supports both the canonical "<namespace>/<name>" keys and legacy name-only
 * keys for compatibility.
 */
export const getSectionResultByViewRef = (
  sectionResults: SectionResultsMap | undefined,
  viewRef: SectionViewRef | undefined
): ViewResult | undefined => {
  if (!sectionResults || !viewRef?.name) {
    return undefined;
  }

  const namespace = viewRef.namespace ?? "";
  const name = viewRef.name;

  const keysToTry = namespace ? [`${namespace}/${name}`, name] : [name];

  for (const key of keysToTry) {
    const result = sectionResults[key];
    if (result) {
      return result;
    }
  }

  for (const [key, result] of Object.entries(sectionResults)) {
    if (result?.name !== name) {
      continue;
    }

    if (namespace && result.namespace && result.namespace !== namespace) {
      continue;
    }

    if (namespace && key.includes("/")) {
      const [keyNamespace, keyName] = key.split("/", 2);
      if (keyNamespace !== namespace || keyName !== name) {
        continue;
      }
    }

    return result;
  }

  return undefined;
};

export type View = {
  id: string;
  name: string;
  namespace?: string;
  spec: any;
  source: string;
  created_by?: string;
  created_at: string;
  updated_at?: string;
  deleted_at?: string;
  last_ran?: string;
  error?: string;
};

export type ViewSummary = {
  id: string;
  namespace?: string;
  name: string;
  title?: string;
  icon?: string;
  ordinal?: number;
  sidebar?: boolean;
  last_ran?: string;
};

export type ViewListItem = {
  id: string;
  name: string;
  namespace?: string;
  title?: string;
  icon?: string;
  ordinal?: number;
};

export const getAllViews = (
  sortBy?: any,
  pageIndex?: number,
  pageSize?: number
) => {
  let url = `/views?select=*&deleted_at=${encodeURIComponent("is.null")}`;

  if (pageIndex !== undefined && pageSize !== undefined) {
    url += `&limit=${pageSize}&offset=${pageIndex * pageSize}`;
  }

  if (sortBy && sortBy.length > 0) {
    const sortFields = sortBy
      .map((sort: any) => `${sort.id}.${sort.desc ? "desc" : "asc"}`)
      .join(",");
    url += `&order=${encodeURIComponent(sortFields)}`;
  } else {
    url += `&order=${encodeURIComponent("created_at.desc")}`;
  }

  return resolvePostGrestRequestWithPagination<View[]>(
    ConfigDB.get(url, {
      headers: {
        Prefer: "count=exact"
      }
    })
  );
};

/**
 * Get the data for a view by its id.
 */
const fetchViewData = async (
  path: string,
  variables?: Record<string, string>,
  headers?: Record<string, string>
): Promise<ViewResult> => {
  const body: { variables?: Record<string, string> } = {
    variables
  };

  const response = await fetch(path, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...headers
    },
    body: JSON.stringify(body)
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw (
      errorData ?? {
        message: `HTTP ${response.status}: ${response.statusText}`,
        status: response.status,
        statusText: response.statusText
      }
    );
  }

  const data = await response.json();

  if (data && typeof data === "object" && (data as Record<string, any>).error) {
    throw data;
  }

  return data;
};

export const getViewDataById = async (
  viewId: string,
  variables?: Record<string, string>,
  headers?: Record<string, string>
): Promise<ViewResult> => {
  return fetchViewData(
    `/api/view/${encodeURIComponent(viewId)}`,
    variables,
    headers
  );
};

export const getViewMetadataById = async (
  viewId: string,
  headers?: Record<string, string>
): Promise<DashboardResponse> => {
  const response = await ViewAPI.get<DashboardResponse>(
    `/metadata/${encodeURIComponent(viewId)}`,
    headers
      ? {
          headers
        }
      : undefined
  );
  return response.data;
};

export const getViewDataByNamespace = async (
  namespace: string,
  name: string,
  variables?: Record<string, string>,
  headers?: Record<string, string>
): Promise<ViewResult> => {
  return fetchViewData(
    `/api/view/${encodeURIComponent(namespace)}/${encodeURIComponent(name)}`,
    variables,
    headers
  );
};

/**
 * Get display plugin variables for a view based on a config
 */
export const getViewDisplayPluginVariables = async (
  viewId: string,
  configId: string
): Promise<Record<string, string>> => {
  const response = await ViewAPI.get(
    `/display-plugin-variables/${viewId}?config_id=${configId}`
  );
  return response.data;
};

export const createView = async (view: Partial<View>) => {
  const response = await ConfigDB.post("/views", view);
  return response;
};

export const updateView = async (id: string, view: Partial<View>) => {
  const response = await ConfigDB.patch(`/views?id=eq.${id}`, view);
  return response;
};

export const deleteView = async (id: string) => {
  const response = await ConfigDB.delete(`/views?id=eq.${id}`);
  return response;
};

export const queryViewTable = async (
  namespace: string,
  name: string,
  columns: ViewColumnDef[],
  searchParams: URLSearchParams,
  requestFingerprint: string
) => {
  const cleanNamespace = namespace.replace(/-/g, "_");
  const cleanName = name.replace(/-/g, "_");
  const tableName = `view_${cleanNamespace}_${cleanName}`;

  let queryString = `?select=*`;

  const pageIndex = parseInt(searchParams.get("pageIndex") ?? "0");
  const pageSize = parseInt(searchParams.get("pageSize") ?? "50");
  const sortBy = searchParams.get("sortBy") ?? "";
  const sortOrder = searchParams.get("sortOrder") ?? "desc";

  queryString += `&limit=${pageSize}&offset=${pageIndex * pageSize}`;

  // Add sorting support
  if (sortBy) {
    const orderClause =
      sortOrder === "desc"
        ? `${sortBy}.${sortOrder}.nullslast`
        : `${sortBy}.${sortOrder}`;
    queryString += `&order=${encodeURIComponent(orderClause)}`;
  }

  for (const [key, value] of searchParams.entries()) {
    if (
      key !== "filter" &&
      key !== "pageIndex" &&
      key !== "pageSize" &&
      key !== "sortBy" &&
      key !== "sortOrder" &&
      value &&
      value.trim()
    ) {
      // Check if this is a labels column that needs JSONB filtering
      const column = columns.find((c) => c.name === key);
      if (column?.type === "labels") {
        const filterQueries = buildLabelFilterQueries(key, value);
        if (filterQueries.length > 0) {
          queryString += `&or=(and(${filterQueries.join(",")}))`;
        }
      } else {
        const filterParam = tristateOutputToQueryFilterParam(value, key);
        if (filterParam) {
          queryString += filterParam;
        }
      }
    }
  }

  // Add requestFingerprint as a filter if provided
  queryString += `&request_fingerprint=eq.${encodeURIComponent(requestFingerprint)}`;

  const response = await resolvePostGrestRequestWithPagination(
    ConfigDB.get(`/${tableName}${queryString}`, {
      headers: {
        Prefer: "count=exact"
      }
    })
  );

  if (response.error) {
    throw response.error;
  }

  const data = response.data;

  const rows = Array.isArray(data) ? data : [];

  return {
    data: rows,
    totalEntries: response.totalEntries
  };
};

export const getViewsForSidebar = async () => {
  const res = await resolvePostGrestRequestWithPagination<ViewSummary[]>(
    ConfigDB.get(
      `/views_summary?sidebar=eq.true&select=id,name,namespace,title,icon,ordinal&order=ordinal.asc,title.asc`
    )
  );
  return res.data ?? [];
};

/**
 * Get all active views for dropdown selection in scope form
 */
export const getViewsSummary = async () => {
  const res = await resolvePostGrestRequestWithPagination<ViewSummary[]>(
    ConfigDB.get(
      `/views_summary?select=id,name,namespace,title&order=namespace.asc,name.asc`
    )
  );
  return res.data ?? [];
};

export const getViewsByConfigId = async (configId: string) => {
  const res = await ViewAPI.get<ViewListItem[]>(`/list?config_id=${configId}`);
  return res.data ?? [];
};

export const getViewIdByNamespaceAndName = async (
  namespace: string,
  name: string
) => {
  const res = await resolvePostGrestRequestWithPagination<ViewSummary[]>(
    ConfigDB.get(
      `/views_summary?namespace=eq.${encodeURIComponent(namespace)}&name=eq.${encodeURIComponent(name)}&select=id`
    )
  );
  return res.data?.[0]?.id;
};

export const getViewIdByName = async (name: string) => {
  const res = await resolvePostGrestRequestWithPagination<ViewSummary[]>(
    ConfigDB.get(`/views_summary?name=eq.${encodeURIComponent(name)}&select=id`)
  );
  return res.data?.[0]?.id;
};
